import React, { Component } from 'react';
import type { ErrorInfo, ReactNode } from 'react';
import { logger } from '../../utils/logger';

import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  AlertTitle
} from '@mui/material';
import { 
  Refresh as RefreshIcon, 
  Home as HomeIcon,
  Dashboard as DashboardIcon 
} from '@mui/icons-material';

interface Props {
  children: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class DashboardErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    logger.error('DashboardErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo
    });

    // Call the onError callback if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box p={2}>
          <Card>
            <CardContent>
                  <Alert severity="error">
                    <AlertTitle>
                      <Box display="flex" alignItems="center" gap={1}>
                        <DashboardIcon />
                        Lỗi Bảng Điều Khiển
                      </Box>
                    </AlertTitle>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      Có lỗi xảy ra khi tải bảng điều khiển. Điều này có thể do vấn đề mạng hoặc lỗi tạm thời của máy chủ.
                    </Typography>
                    
                    {import.meta.env.DEV && this.state.error && (
                      <Box mt={2}>
                        <Typography variant="caption" component="pre" sx={{ 
                          backgroundColor: 'rgba(0,0,0,0.1)', 
                          p: 1, 
                          borderRadius: 1,
                          fontSize: '0.75rem',
                          overflow: 'auto'
                        }}>
                          {this.state.error.toString()}
                          {this.state.errorInfo?.componentStack}
                        </Typography>
                      </Box>
                    )}
                    
                    <Box mt={2} display="flex" gap={1}>
                      <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={this.handleRetry}
                        size="small"
                      >
                        Tải Lại Bảng Điều Khiển
                      </Button>
                      <Button
                        variant="outlined"
                        startIcon={<HomeIcon />}
                        onClick={this.handleGoHome}
                        size="small"
                      >
                        Về Trang Chủ
                      </Button>
                    </Box>
                  </Alert>
            </CardContent>
          </Card>
        </Box>
      );
    }

    return this.props.children;
  }
}

// Hook for dashboard-specific error handling
export const useDashboardErrorHandler = () => {
  const [error, setError] = React.useState<Error | null>(null);

  const resetError = React.useCallback(() => {
    setError(null);
  }, []);

  const captureError = React.useCallback((error: Error) => {
    logger.error('Dashboard error captured:', error);
    setError(error);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { captureError, resetError };
};
